import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

import React, { useState, useEffect, useRef } from 'react';
import { Menu } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GridViewIcon from '@mui/icons-material/GridView';
import { useConfig } from '../../../utils/useConfig';

import { Category } from '../../../models/Category';
import NavBarCategory from './NavBarCategory';

type Props = {
  categories: Category[];
  isScrolled?: boolean;
};

function NavBarCategories({ categories, isScrolled = false }: Props) {
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const categoriesContainerRef = useRef<HTMLDivElement>(null);
  const config = useConfig();
  const mainColor = config?.theme?.colors?.primary || '#3B82F6';

  // Ilość widocznych kategorii zależy od szerokości ekranu i czy strona jest przewinięta
  const getVisibleCategoriesCount = () => {
    if (windowWidth < 640) return 2; // mobile
    if (windowWidth < 768) return 3; // small tablets
    if (windowWidth < 1024) return 4; // tablets
    if (windowWidth < 1280) return 5; // small laptops
    return 6; // desktops
  };

  const visibleCategoriesCount = getVisibleCategoriesCount();
  const hasHiddenCategories = categories?.length > visibleCategoriesCount;

  // Aktualizuj szerokość okna przy zmianie rozmiaru
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Zamykaj menu "Więcej kategorii" po kliknięciu poza nim
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        categoriesContainerRef.current && 
        !categoriesContainerRef.current.contains(event.target as Node)
      ) {
        setShowAllCategories(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Kategorii do wyświetlenia bezpośrednio w pasku
  const visibleCategories = categories?.slice(0, visibleCategoriesCount) || [];
  
  // Pozostałe kategorie do wyświetlenia w rozwijalnym menu
  const moreCategories = categories?.slice(visibleCategoriesCount) || [];

  return (
    <div 
      ref={categoriesContainerRef} 
      className={`relative flex justify-center w-full transition-all duration-300 ${
        isScrolled ? 'opacity-95' : 'opacity-100'
      }`}
    >
      <div 
        className={`flex items-center justify-center space-x-1 md:space-x-2 z-20 ${
          isScrolled ? 'py-1' : 'py-2'
        }`}
      >
        {visibleCategories.map((category) => (
          <Menu
            key={category.id}
            as="div"
            onMouseLeave={() => {
              const activeEl = document.activeElement;
              if (
                activeEl instanceof HTMLElement &&
                activeEl.tagName === 'BUTTON'
              ) {
                activeEl.blur();
              }
            }}
            className="relative inline-block text-left"
          >
            <NavBarCategory category={category} />
          </Menu>
        ))}
        
        {hasHiddenCategories && (
          <div className="relative inline-block text-left">
            <motion.button
              onClick={() => setShowAllCategories(!showAllCategories)}
              className={`flex items-center space-x-1 transition-all duration-200 px-3 py-2 rounded-lg
                hover:bg-slate-50 hover:shadow-sm shrink-0 text-slate-700 ${
                  showAllCategories ? 'bg-slate-50 font-medium text-slate-800 shadow-sm' : ''
                }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-expanded={showAllCategories}
              aria-haspopup="true"
              style={{ color: showAllCategories ? mainColor : '' }}
            >
              <GridViewIcon fontSize="small" />
              <span className="whitespace-nowrap hidden sm:inline">Więcej kategorii</span>
              <span className="whitespace-nowrap sm:hidden">Więcej</span>
              <KeyboardArrowDownIcon 
                fontSize="small" 
                className={`transition-transform ${showAllCategories ? 'rotate-180' : ''}`}
              />
            </motion.button>

            <AnimatePresence>
              {showAllCategories && (
                <>
                  {/* Przyciemnienie tła */}
                  <motion.div
                    className="fixed inset-0 bg-black/20 z-30 lg:hidden"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowAllCategories(false)}
                  />

                  {/* Menu z dodatkowymi kategoriami */}
                  <motion.div
                    className="absolute right-0 mt-2 origin-top-right bg-white rounded-lg shadow-lg z-40 
                      overflow-hidden border border-gray-100"
                    style={{ 
                      width: `${windowWidth < 640 ? '280px' : '320px'}`, 
                      maxHeight: 'calc(100vh - 200px)' 
                    }}
                    initial={{ opacity: 0, y: -20, scale: 0.95 }}
                    animate={{ 
                      opacity: 1, 
                      y: 0, 
                      scale: 1,
                      transition: { duration: 0.2 }
                    }}
                    exit={{ 
                      opacity: 0, 
                      y: -10, 
                      scale: 0.95,
                      transition: { duration: 0.15 }
                    }}
                  >
                    <div className="p-2 border-b border-gray-100 flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-700">Wszystkie kategorie</h3>
                      <span 
                        className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
                      >
                        {categories?.length || 0}
                      </span>
                    </div>

                    <div className="overflow-y-auto max-h-[60vh]">
                      {/* Najpierw pokazujemy elementy, które są już widoczne w głównym menu (ale w innym układzie) */}
                      <div className="p-1">
                        {visibleCategories.map((category, index) => (
                          <MoreCategoryItem 
                            key={category.id} 
                            category={category}
                            delay={index * 0.02}
                            mainColor={mainColor}
                            onClick={() => setShowAllCategories(false)}
                          />
                        ))}
                      </div>

                      {/* Następnie pokazujemy ukryte elementy */}
                      {moreCategories.length > 0 && (
                        <>
                          <div className="border-t border-gray-100 my-1"></div>
                          <div className="p-1">
                            {moreCategories.map((category, index) => (
                              <MoreCategoryItem 
                                key={category.id} 
                                category={category}
                                delay={(visibleCategories.length + index) * 0.02}
                                mainColor={mainColor}
                                onClick={() => setShowAllCategories(false)}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}

// Komponent dla elementów w menu "Więcej kategorii"
function MoreCategoryItem({ category, delay, mainColor, onClick }: { 
  category: Category, 
  delay: number,
  mainColor: string,
  onClick: () => void
}) {
  const [showSubmenu, setShowSubmenu] = useState(false);
  const hasSubcategories = category.subCategories && category.subCategories.length > 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -5 }}
      animate={{ 
        opacity: 1, 
        x: 0, 
        transition: { delay, duration: 0.2 }
      }}
      className="relative"
    >
      <motion.a
        href={`/szukaj?categoryId=${category.id}`}
        className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-md"
        onClick={(e) => {
          if (hasSubcategories) {
            e.preventDefault();
            setShowSubmenu(!showSubmenu);
          } else {
            onClick();
          }
        }}
        whileHover={{ x: 3 }}
      >
        <div className="flex items-center gap-2">
          {category.image && (
            <div className="w-5 h-5 shrink-0">
              <img 
                src={category.image} 
                alt={category.name} 
                className="w-full h-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
          <span className="text-xs sm:text-sm">{category.name}</span>
        </div>
        {hasSubcategories && (
          <KeyboardArrowDownIcon 
            fontSize="small" 
            className={`text-gray-400 transition-transform ${showSubmenu ? 'rotate-180' : ''}`}
          />
        )}
      </motion.a>
      
      <AnimatePresence>
        {showSubmenu && hasSubcategories && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden pl-3"
          >
            <div className="border-l-2 border-gray-100 ml-2 pl-3 py-1">
              {category.subCategories?.map((subcategory, idx) => (
                <motion.a
                  key={subcategory.id}
                  href={`/szukaj?categoryId=${subcategory.id}`}
                  onClick={() => onClick()}
                  className="flex items-center py-2 px-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded"
                  whileHover={{ x: 2 }}
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    transition: { delay: 0.1 + idx * 0.03 }
                  }}
                >
                  {subcategory.name}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default NavBarCategories;
